import AnnualReportForm from "../../../model/AnnualReportForm";
import {useState} from "react";
import PapersPublished from "../../../model/PapersPublished";
import {DOI_LINK} from "../../../AnnualReportConstants";

const PAPERS_PUBLISHED_QUESTION_TEXT = (
   <>
      Please provide the first author, title and any <a href={DOI_LINK}>DOI</a> links to all publications from this
      Research Project since your last Annual Project Report, if any.
   </>
);
const PAPERS_PUBLISHED_REQUIREMENT_CLARIFICATION_URL = `https://community.ukbiobank.ac.uk/hc/en-gb/articles/16594178325277-Submitting-publications-and-use-of-UK-Biobank-images`;
const PAPERS_PUBLISHED_REQUIREMENT_CLARIFICATION = (<>
      Please note that it is a requirement of the Material Transfer Agreement (MTA) that you inform us of
      results being published at least 2 weeks in advance of publication. This is required for results that are being
      made publicly available, including (but not limited to) manuscripts, pre-prints, social media posts and blog
      posts.
      Please see <a href={PAPERS_PUBLISHED_REQUIREMENT_CLARIFICATION_URL}>here</a> for acknowledgement requirements.
   </>
);

/**
 * Props for this component
 */
interface PapersPublishedQuestionProps {
   form: AnnualReportForm,
   isLocked: boolean,
}

/**
 * Collaborator Annual Report question regarding paper being produced in this project.
 *
 * Used in: {@link CollaboratorAnnualReportForm|Collaborator annual report}
 *
 * @param form An {@link AnnualReportForm} object with a saved report (Could be empty if it's a new report)
 * @param isLocked A boolean to indicate if the report is locked for editing or not
 */
export default function PapersPublishedQuestion({form, isLocked}: PapersPublishedQuestionProps) {
   const [papersPublished, setPapersPublished] = useState<PapersPublished[]>(splitPapers(form.doiReferences, form.locked));

   /**
    * When a row is updated, it will store the new value in its respective element. If the element being updated
    * was the empty one (The array will always have one), it will create a new element at the end of the array.
    *
    * @param index The index of the Published Paper in the array
    * @param field Which field is being edited (Name, Title or DOI)
    * @param value The new value of the field
    */
   const updatePapers = (index: number, field: 'name' | 'title' | 'doi', value: string): void => {
      // Update entry
      const temp: PapersPublished[] = [...papersPublished];
      temp[index][field] = value;

      // If it's the last paper and user starts typing, add a new one
      if (index === temp.length - 1 && value !== '') {
         temp.push({name: '', title: '', doi: ''});
      }

      form.doiReferences = mergePapers(temp);
      setPapersPublished(temp);
   }

   /**
    * Remove a paper from the array
    *
    * @param index The index of the element we wish to delete
    */
   const removePaper = (index: number): void => {
      const temp: PapersPublished[] = [...papersPublished];
      temp.splice(index, 1);

      form.doiReferences = mergePapers(temp);
      setPapersPublished(temp);
   }

   return <div id="PapersQ" className="annual-report-question">
      <label id="PapersQMainText" className="question-text">{PAPERS_PUBLISHED_QUESTION_TEXT}</label>
      <label id="PapersQSubText" className="extra-info">{PAPERS_PUBLISHED_REQUIREMENT_CLARIFICATION}</label>
      {/*Third Party processors' entries*/}
      {papersPublished.map((paper, index) => (
         <div id={`PapersQPaper_${index}`} key={index}
              className="multi-entry-question">
            <div id={`PapersQEntryArea_${index}`}>
               <input
                  id={`PapersQPaperName_${index}`}
                  type="text"
                  className="form-control"
                  placeholder="Author's first name"
                  value={paper.name}
                  disabled={isLocked}
                  onChange={(e) => updatePapers(index, 'name', e.target.value)}
                  required={(index !== papersPublished.length - 1 && !isLocked)}
               />
               <input
                  id={`PapersQPaperTitle_${index}`}
                  type="text"
                  className="form-control"
                  placeholder="Title"
                  value={paper.title}
                  disabled={isLocked}
                  onChange={(e) => updatePapers(index, 'title', e.target.value)}
                  required={(index !== papersPublished.length - 1 && !isLocked)}
               />
               <input
                  id={`PapersQPaperDOI_${index}`}
                  type="text"
                  className="form-control"
                  placeholder="DOI"
                  value={paper.doi}
                  disabled={isLocked}
                  onChange={(e) => updatePapers(index, 'doi', e.target.value)}
                  required={(index !== papersPublished.length - 1 && !isLocked)}
               />
            </div>
            {/*Show delete button if this is not the empty element*/}
            {(index !== papersPublished.length - 1 && !isLocked) ?
               (<button id={`PapersQPaperRemove_${index}`}
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={() => removePaper(index)}></button>
               ) : null
            }
         </div>
      ))}
   </div>;
}


/**
 * This method will gather the data from the papers and merge it into a single string
 *
 * @param papers The array of papers
 */
function mergePapers(papers: PapersPublished[]): string {
   let stringWithPapers: string = "";

   for (let i = 0; i < papers.length - 1; i++) {
      // Don't include the ';' at the beginning
      if (i === 0) {
         stringWithPapers = `${encodeURIComponent(papers[i].name)}|${encodeURIComponent(papers[i].title)}|${encodeURIComponent(papers[i].doi)}`;
         continue;
      }
      stringWithPapers = `${stringWithPapers};${encodeURIComponent(papers[i].name)}|${encodeURIComponent(papers[i].title)}|${encodeURIComponent(papers[i].doi)}`;
   }

   return stringWithPapers;
}

/**
 * This function will separate the papers published associated with this report
 *
 * @param formString The papers on the form (Can be null)
 * @param isLocked A boolean to show if the report is blocked or not so we can show the empty row
 */
function splitPapers(formString: string | null, isLocked: boolean): PapersPublished[] {
   const arrayOfPapers: PapersPublished[] = [];
   if (formString) {
      // Split into individual processors
      const splitString: string[] = formString.split(';');

      splitString.forEach((line: string) => {
         // Split into individual parts
         const parts: string[] = line.split('|');
         arrayOfPapers.push({
            name: decodeURIComponent(parts[0]),
            title: decodeURIComponent(parts[1]),
            doi: decodeURIComponent(parts[2])
         });
      })
   }

   if (!isLocked) {
      arrayOfPapers.push({name: '', title: '', doi: ''})
   }

   return arrayOfPapers;
}
